import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { tap, catchError, firstValueFrom, switchMap } from 'rxjs';
import {
  Session,
  SourceSession,
  ReportType,
  SessionMemberRole,
  Claimant,
  ReportGenerationStatusValue,
  SessionReportGenerationStatus,
  MmiGateCheckState,
} from '../interfaces/session.interface';
import { inject } from '@angular/core';
import {
  SessionService,
  PhysicianOption,
  SessionsPageResponse,
} from '../services/session.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Output } from '../interfaces/task.interface';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import {
  AiScribeContentOutputType,
  AiScribeContents,
  AiScribeJob,
  AiScribeStatus,
  SourceAiScribeJob,
  selectPrimaryScribeContent,
  SourceSubReportStatus,
  isSiblingContentOutputType,
  SubReportStatus,
  SUB_REPORT_STATUS,
  isSubReportStatus,
} from '../interfaces/aiScribeJob.interface';
import { AiScribeService } from '../services/aiScribe.service';
import { mapSessionMember } from '../utils/session-member.utils';
import { UploadProgressService } from '../services/upload-progress.service';

type SessionsState = {
  sessions: Session[];
  sessionsTotal: number;
  physicianOptions: PhysicianOption[];
  isLoading: boolean;
  activeSession: Session | null;
};

const initialState: SessionsState = {
  sessions: [],
  sessionsTotal: 0,
  physicianOptions: [],
  isLoading: false,
  activeSession: null,
};

const CASES_TABLE_INCLUDE = {
  claimant: true,
  SessionMember: {
    where: { role: 'ASSIGNEE' },
    include: {
      user: {
        include: {
          qmeProfile: true,
        },
      },
    },
  },
};

const TRIAL_CASE_LIMIT_MESSAGE =
  'You are on Complimentary Access and have reached the case limit. Contact Long Health to continue.';

function mapSourceSession(s: SourceSession): Session {
  return {
    ...s,
    dateOfInjury: stripTimeFromDate(s.dateOfInjury) as Session['dateOfInjury'],
    claimant: s.claimant
      ? {
          ...s.claimant,
          dateOfBirth: stripTimeFromDate(s.claimant.dateOfBirth),
        }
      : undefined,
    sessionMembers: s.SessionMember ?? [],
  } as Session;
}

function stripTimeFromDate(
  value: Date | string | null | undefined,
): string | null | undefined {
  if (value === null || value === undefined || value === '') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString().split('T')[0];
  }

  return value.split('T')[0];
}

function getUserFacingErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const response = (error as { error?: unknown }).error;
  if (response && typeof response === 'object') {
    const message = (response as { message?: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }

  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' ? message : fallback;
}

// A job can accumulate multiple rows of the same outputType (one per
// regeneration, each with an incremented `version` per the
// `sessionId/aiScribeId/outputType/version` uniqueness constraint) — the
// highest version per type is the current one.
function latestContentIdByOutputType(
  contents: AiScribeContents[],
): Partial<Record<AiScribeContentOutputType, string>> {
  const latestByType = new Map<AiScribeContentOutputType, AiScribeContents>();
  for (const c of contents) {
    if (!c?.outputType || !c.id) continue;
    const current = latestByType.get(c.outputType);
    if (!current || (c.version ?? 0) > (current.version ?? 0)) {
      latestByType.set(c.outputType, c);
    }
  }
  const result: Partial<Record<AiScribeContentOutputType, string>> = {};
  for (const [type, c] of latestByType) {
    result[type] = c.id as string;
  }
  return result;
}


// Fold the API's `subReportStatuses` rows into a map keyed by outputType, the
// same collapse latestContentIdByOutputType does for content rows.
//
// ⚠️ A FORM WITH NO ROW MUST STAY ABSENT FROM THE MAP. Absent means no async
// workflow has ever evaluated this scribe for that form — every scribe
// predating the async flow, and every org still on the legacy path — and the
// tab is hidden entirely. It is NOT the same as 'NOT_REQUIRED', which means the
// gate ran and declined and DOES show a tab. Do not default missing entries to
// anything here; the distinction is the whole reason the backend made the row's
// absence a first-class state rather than a nullable column.
function subReportStatusesByOutputType(
  rows: SourceSubReportStatus[] | undefined,
): Partial<Record<AiScribeContentOutputType, SubReportStatus>> {
  const result: Partial<Record<AiScribeContentOutputType, SubReportStatus>> =
    {};
  for (const row of rows ?? []) {
    if (!row?.outputType || !row.status) continue;
    // Only keys the UI knows how to render. An unrecognised outputType from a
    // newer API would otherwise land in the map and be counted as "this form
    // has a status", offering a tab with no config behind it.
    if (!isSiblingContentOutputType(row.outputType)) continue;
    // And only values this build can render. A status it does not recognise —
    // the empty string that exists in production, or one a newer backend
    // added — is the ABSENCE of an answer, not an answer, so it must not key
    // the map at all. See isSubReportStatus.
    if (!isSubReportStatus(row.status)) continue;
    result[row.outputType] = row.status;
  }
  return result;
}

/** error_code per FAILED sibling row — the failure reason's key. */
function subReportErrorCodesByOutputType(
  rows: SourceSubReportStatus[] | undefined,
): Partial<Record<AiScribeContentOutputType, string>> {
  const result: Partial<Record<AiScribeContentOutputType, string>> = {};
  for (const row of rows ?? []) {
    if (
      row?.outputType &&
      isSiblingContentOutputType(row.outputType) &&
      row.status === SUB_REPORT_STATUS.FAILED &&
      row.errorCode
    ) {
      result[row.outputType] = row.errorCode;
    }
  }
  return result;
}

export function mapSourceToAiScribeJob(job: SourceAiScribeJob): AiScribeJob {
  // selectPrimaryScribeContent, not [0]. A scribe job can now own several
  // content rows (the PR report's LEGACY row plus an RFA row), and the API
  // include has no ORDER BY — so [0] was arbitrary and the PR tab could render
  // the RFA's JSON payload. output_type stays LEGACY for PR reports forever, so
  // "the row that isn't RFA" is sufficient and nothing downstream of this
  // collapse has to change.
  const content = selectPrimaryScribeContent(job.AiScribeContents);
  const contentIdByOutputType = latestContentIdByOutputType(
    job.AiScribeContents ?? [],
  );
  const subReportStatuses = subReportStatusesByOutputType(
    job.subReportStatuses,
  );
  const subReportErrorCodes = subReportErrorCodesByOutputType(
    job.subReportStatuses,
  );
  const logs = job.AiScribeLogs || job.ai_scribe_logs || [];
  const latestLogStatus =
    logs.length > 0
      ? [...logs].sort(
          (a, b) =>
            new Date(b.createdTime || b.createdAt || 0).getTime() -
            new Date(a.createdTime || a.createdAt || 0).getTime(),
        )[0]?.status
      : undefined;
  // The backend returns raw status strings rather than a validated enum
  // member; the values are trusted to match `AiScribeStatus` at this boundary.
  const finalStatus = (job.status ||
    job.latestStatus ||
    latestLogStatus ||
    AiScribeStatus.NEW) as AiScribeStatus;

  return {
    ...job,
    status: finalStatus,
    AiScribeContents: {
      ...content,
      inputText: content?.inputText || '',
      generatedOutput: content?.generatedOutput || { output: '' },
    },
    contentIdByOutputType,
    subReportStatuses,
    subReportErrorCodes,
  };
}

export function mergeHydratedScribeContent(
  previous: AiScribeContents | undefined,
  next: AiScribeContents | undefined,
): AiScribeContents | undefined {
  if (!next) return previous;
  if (!previous) return next;

  const previousId = previous.id;
  const nextId = next.id;
  if (!previousId || !nextId || previousId !== nextId) return next;

  const isNonEmpty = (value: unknown): value is string =>
    typeof value === 'string' && value.length > 0;

  const merged: AiScribeContents = { ...next };

  const inputIsOffloaded = !!next.inputS3Key && !!next.inputS3Bucket;
  if (
    inputIsOffloaded &&
    !isNonEmpty(next.inputText) &&
    isNonEmpty(previous.inputText)
  ) {
    merged.inputText = previous.inputText;
  }

  const outputIsOffloaded = !!next.outputS3Key && !!next.outputS3Bucket;
  if (
    outputIsOffloaded &&
    !isNonEmpty(next.generatedOutput?.output) &&
    isNonEmpty(previous.generatedOutput?.output)
  ) {
    merged.generatedOutput = previous.generatedOutput;
  }

  return merged;
}

/**
 * Apply mergeHydratedScribeContent across a freshly-mapped job list.
 *
 * Every path that rebuilds the job array from a server payload — the session
 * GET, fetchActiveSessionAiScribeJobs, updateJobMetadata — runs the rows
 * through mapSourceToAiScribeJob, which substitutes an empty body for the
 * stripped one. Without this, each of those replaces a body this client
 * fetched from S3 with `''`.
 *
 * refreshAiScribeJobFromEvent runs all three in sequence for a single
 * websocket event, so guarding only the last one is not enough: the earlier
 * two have already blanked the value that the last one would have preserved.
 */
function preserveHydratedScribeBodies(
  previousJobs: AiScribeJob[] | undefined,
  nextJobs: AiScribeJob[],
): AiScribeJob[] {
  if (!previousJobs?.length) return nextJobs;

  const previousById = new Map(
    previousJobs.map((job) => [job.aiScribeId, job] as const),
  );

  return nextJobs.map((job) => {
    const previous = previousById.get(job.aiScribeId);
    if (!previous) return job;

    const merged = mergeHydratedScribeContent(
      previous.AiScribeContents,
      job.AiScribeContents,
    );
    if (merged === job.AiScribeContents) return job;
    return { ...job, AiScribeContents: merged as AiScribeContents };
  });
}

function hasReviewOfRecordsBody(output: Output | undefined | null): boolean {
  if (!output?.value) return false;
  const text = (output.value as Record<string, unknown>)['review_of_records'];
  return typeof text === 'string' && text.trim().length > 0;
}

/**
 * Session refetch often returns the RoR output row before S3 hydration fills
 * `value.review_of_records`. Without this, the Records tab hides for ~hydrate ms.
 */
function mergeReviewOfRecordsForActivePatch(
  active: Output | undefined,
  previous: Output | undefined,
): Output | undefined {
  if (!active) return previous;
  if (hasReviewOfRecordsBody(active)) return active;
  if (!previous || !hasReviewOfRecordsBody(previous)) return active;
  return {
    ...active,
    value: {
      ...(active.value ?? {}),
      review_of_records: (previous.value as Record<string, unknown>)[
        'review_of_records'
      ],
    },
  };
}

function hasSummaryBody(output: Output | undefined | null): boolean {
  if (!output?.value) return false;
  const text = (output.value as Record<string, unknown>)['summary'];
  return typeof text === 'string' && text.trim().length > 0;
}

/**
 * Same fix as mergeReviewOfRecordsForActivePatch, for the Summary tab.
 * The summary body lives in S3 (the row's `value` is `{}`), so every
 * same-session refetch (setActiveSession — fired by websocket events such as
 * report_generation_status COMPLETED, reconnect reconciliation, etc.) replaced
 * the hydrated summary with an empty row, and the tab blanked until the lazy
 * S3 hydrate landed again: the flicker. Keep the previous text meanwhile; the
 * summary component's hydrateOutputType() then swaps in the fresh body.
 */
function mergeSummaryForActivePatch(
  active: Output | undefined,
  previous: Output | undefined,
): Output | undefined {
  if (!active) return previous;
  if (hasSummaryBody(active)) return active;
  if (!previous || !hasSummaryBody(previous)) return active;
  return {
    ...active,
    value: {
      ...(active.value ?? {}),
      summary: (previous.value as Record<string, unknown>)['summary'],
    },
  };
}

function resolveActiveAiScribeJob(
  currentActiveJob: AiScribeJob | undefined,
  jobs: AiScribeJob[],
): AiScribeJob | undefined {
  if (!currentActiveJob?.aiScribeId) {
    return currentActiveJob;
  }

  return (
    jobs.find((job) => job.aiScribeId === currentActiveJob.aiScribeId) ??
    currentActiveJob
  );
}

function updateSessionAiScribeJobs(
  session: Session,
  jobs: AiScribeJob[],
): Session {
  const resolvedActiveAiScribeJob = resolveActiveAiScribeJob(
    session.activeAiScribeJob,
    jobs,
  );

  return {
    ...session,
    aiScribeJobs: jobs,
    activeAiScribeJob: resolvedActiveAiScribeJob as AiScribeJob,
  };
}

export const SessionStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      sessionService = inject(SessionService),
      notification = inject(NzNotificationService),
      messageService = inject(NzMessageService),
      aiScribeService = inject(AiScribeService),
      uploadProgressService = inject(UploadProgressService),
    ) => {
      // Tracks in-flight lazy hydrations keyed by `${sessionId}:${outputId}` so
      // repeated calls from viewer components dedupe to a single network request.
      const inFlightHydrations = new Map<string, Promise<void>>();
      const hydratedOutputIds = new Set<string>();

      // Authoritative per-report freshness (`mmiGateState`) is computed by the
      // server from the tracked input document set of each report's last run,
      // it is not derivable from anything already in the store, so it has to be re-read
      // whenever the inputs it compares against change.
      const REPORT_FRESHNESS_DEBOUNCE_MS = 1500;
      let reportFreshnessTimer: ReturnType<typeof setTimeout> | null = null;

      const fetchReportFreshness = (sessionId: string) => {
        sessionService.getMmiGateState(sessionId).subscribe({
          next: (mmiGateState) => {
            // A response that arrives after the user has switched sessions must
            // be discarded rather than applied to the wrong one.
            const current = store.activeSession();
            if (current?.id !== sessionId) return;
            patchState(store, {
              activeSession: { ...current, mmiGateState },
            });
          },
          error: (err) => {
            console.error('Failed to fetch MMI gate state', err);
          },
        });
      };

      // Pending docx template selections: sessionId → generationProfileTargetId.
      // Stored in-memory at case creation and consumed at first generation trigger.
      // After the first generation the server upserts the GenerationSelection row
      // and owns the pick for subsequent runs — no DB write needed here.
      //
      // TODO: if the UX ever requires the pick to survive a page refresh between
      // case creation and first generation, replace this Map with a lightweight
      // PUT /generation-templates/selection call at creation time (the service
      // method saveGenerationSelection is already wired; just un-comment the call
      // in session.component.ts where the TODO placeholder is left).
      const pendingDocxTemplateSelections = new Map<string, string>();

      return {
        // Method to add a session with file uploads or adds files to an existing session
        async addSession(
          connection_id: string,
          files: NzUploadFile[],
          sessionName: string,
          case_session_id?: string,
          specialty?: string,
          reportType?: ReportType,
          fileHashes?: Record<string, string>,
          selectionId?: string,
        ): Promise<Session | null> {
          try {
            let session: SourceSession | Session | null = null;
            let isExistingSession = false;

            // Filter out invalid/empty files before proceeding
            const validFiles = (files || []).filter(
              (f) =>
                !!f &&
                typeof f.size === 'number' &&
                f.size > 0 &&
                !!f.name &&
                !!f.type,
            );

            // Check if case_session_id is provided, if not create a new session
            if (!case_session_id) {
              session = await firstValueFrom(
                sessionService.createSession(
                  sessionName,
                  specialty,
                  reportType,
                  selectionId,
                ),
              );
              case_session_id = session.id;
            } else if (case_session_id) {
              session =
                store.sessions().find((s) => s.id === case_session_id) ?? null;
              case_session_id = session?.id;
              isExistingSession = true;
            }

            if (!session) {
              messageService.error('Failed to create or find session.');
              return null;
            }

            if (!isExistingSession) {
              patchState(store, (state) => ({
                sessions: [
                  {
                    ...session,
                    sessionMembers: (
                      (session as SourceSession).SessionMember ?? []
                    ).map(mapSessionMember),
                  },
                  ...state.sessions,
                ],
                sessionsTotal: state.sessionsTotal + 1,
              }));
            }

            // Only create a batch and upload when there are actual files to process.
            // Creating a batch with no files would mark it isExtracted=true, causing
            // all subsequent real uploads to be treated as non-first batches (isExtracted=false).
            let batchId: string | undefined;
            let successfulUploads: any[] = [];
            let failedUploads: any[] = [];

            if (validFiles.length > 0) {
              // Initiate batch upload for all files in this session
              try {
                const batchResponse = await firstValueFrom(
                  sessionService.initiateBatchUpload(session.id, connection_id),
                );
                batchId = batchResponse.batchId;
              } catch (error) {
                console.error('Error initiating batch upload:', error);
                // Continue without batch_id (backward compatibility)
              }

              // Register every file in the progress service before starting
              const sessionId = session.id;
              validFiles.forEach((file) => {
                uploadProgressService.register({
                  uid: file.uid,
                  fileName: file.name ?? '',
                  sessionId,
                  fileSize: file.size,
                  fileType: file.type,
                  progress: 0,
                  status: 'uploading',
                });
              });

              // Upload all files with the same batch_id.
              // Each file is patched into the store immediately when it finishes
              // so the user can click it without waiting for the whole batch.
              const uploadPromises = validFiles.map((file) =>
                sessionService
                  .handleMultipartUpload(
                    file,
                    sessionId,
                    batchId,
                    fileHashes?.[file.uid] ?? null,
                    (percent) =>
                      uploadProgressService.updateProgress(
                        sessionId,
                        file.uid,
                        percent,
                      ),
                  )
                  .then((result) => {
                    if (result.success && result.document) {
                      uploadProgressService.markUploaded(sessionId, file.uid);
                      // Patch this document into the store immediately — don't
                      // wait for the rest of the batch to finish.
                      if (store.activeSession()?.id === sessionId) {
                        const activeSession = store.activeSession();
                        if (activeSession) {
                          patchState(store, {
                            activeSession: {
                              ...activeSession,
                              documents: [
                                ...(activeSession.documents || []),
                                result.document,
                              ],
                            },
                          });
                        }
                      }
                    } else {
                      uploadProgressService.markFailed(sessionId, file.uid);
                    }
                    return result;
                  }),
              );
              Promise.all(uploadPromises)
                .then((results) => {
                  const successfulUploads = results
                    .map((r) => r.document)
                    .filter(
                      (doc): doc is NonNullable<typeof doc> =>
                        doc !== null && doc !== undefined,
                    );

                  const failedUploads = results.filter(
                    (r) => !r.success || r.document === null,
                  );

                  if (failedUploads.length > 0) {
                    messageService.error(
                      `Failed to upload ${failedUploads.length} documents.`,
                    );
                  }

                  // finalize batch AFTER all uploads complete
                  if (batchId) {
                    sessionService
                      .finalizeBatch(session.id, batchId)
                      .subscribe();
                  }

                  if (successfulUploads.length > 0) {
                    sessionService
                      .triggerOcrForSession(session.id, connection_id)
                      .subscribe();
                  }
                })
                .catch((err) => {
                  console.error('Background upload error:', err);
                });
            }

            return session;
          } catch (error) {
            console.error('Error during session upload:', error);
            const message = getUserFacingErrorMessage(
              error,
              'An unexpected error occurred while creating your case.',
            );
            if (message === TRIAL_CASE_LIMIT_MESSAGE) {
              notification.error(
                'Complimentary Access limit reached',
                message,
                {
                  nzDuration: 0,
                },
              );
            } else {
              messageService.error(message);
            }
            return null;
          }
        },
        triggerOcrForSession(sessionId: string, connection_id?: string) {
          sessionService
            .triggerOcrForSession(sessionId, connection_id)
            .subscribe({
              next: (response) => {
                if (response instanceof Error) {
                  console.error('Error triggering OCR:', response);
                }
              },
              error: (error) => {
                console.error('Error triggering OCR:', error);
              },
            });
        },
        removeSession(sessionId: string) {
          patchState(store, (state) => {
            const existed = state.sessions.some(
              (session) => session.id === sessionId,
            );
            return {
              sessions: state.sessions.filter(
                (session) => session.id !== sessionId,
              ),
              sessionsTotal: existed
                ? Math.max(0, state.sessionsTotal - 1)
                : state.sessionsTotal,
            };
          });
        },
        setActiveSession(session: { id: string }) {
          hydratedOutputIds.clear();
          inFlightHydrations.clear();
          if (reportFreshnessTimer) {
            clearTimeout(reportFreshnessTimer);
            reportFreshnessTimer = null;
          }
          // Return the observable so callers can wait for the session payload
          return sessionService
            .getSession(session.id, {
              documents: { include: { ocrOutput: true } },
              task: {
                orderBy: { createdAt: 'desc' },
                include: {
                  output: {
                    orderBy: { createdAt: 'desc' },
                  },
                },
              },
              chat_sessions: {
                orderBy: { startedAt: 'desc' },
                take: 1,
                include: { messages: true },
              },
              ai_scribe: {
                orderBy: { createdAt: 'desc' },
                include: {
                  AiScribeContents: {
                    // asc, not desc: selectPrimaryScribeContent keeps array
                    // order when choosing between output types, so oldest-first
                    // keeps the transcript row (written first) as the collapsed
                    // row. Newest-first hands it to a PR row, whose input S3
                    // refs are null on older scribes.
                    orderBy: { createdAt: 'asc' },
                    select: {
                      id: true,
                      aiScribeId: true,
                      sessionId: true,
                      outputType: true,
                      outputFormat: true,
                      version: true,
                      createdAt: true,
                      updatedAt: true,
                      inputS3Key: true,
                      inputS3Bucket: true,
                      outputS3Key: true,
                      outputS3Bucket: true,
                      // Asked for, but the API only returns it on rows with no
                      // output S3 refs — i.e. scribes written before outputs
                      // were offloaded, which have no other source for their
                      // body. Everything newer comes back without it and is
                      // hydrated from S3 on demand. inputText stays out
                      // entirely; the transcript is always fetched via the
                      // signed-url route.
                      generatedOutput: true,
                    },
                  },
                  ai_scribe_logs: true,
                  FileAsset: true,
                  // Sibling-form tab state (RFA / Injection / Work Status).
                  // Without it a fresh session load maps every scribe to an
                  // empty status map and hides every sibling tab; only the
                  // websocket refresh (GET .../ai-scribe) carried it before.
                  subReportStatuses: {
                    select: {
                      outputType: true,
                      status: true,
                      errorCode: true,
                      updatedAt: true,
                    },
                  },
                },
              },
              claimant: true,
              reportGenerationStatuses: true,
              SessionMember: {
                include: {
                  user: {
                    include: {
                      qmeProfile: true,
                    },
                  },
                },
              },
            })
            .pipe(
              tap((sessionData: any) => {
                const totalStart = performance.now();
                const normalizedSessionData = {
                  ...sessionData,
                  dateOfInjury: stripTimeFromDate(sessionData.dateOfInjury),
                  claimant: sessionData.claimant
                    ? {
                        ...sessionData.claimant,
                        dateOfBirth: stripTimeFromDate(
                          sessionData.claimant.dateOfBirth,
                        ),
                      }
                    : undefined,
                };
                const isPiSession =
                  (normalizedSessionData.reportType ??
                    normalizedSessionData.report_type) ===
                  'personal_injury_report';
                let activeSummary: any = undefined;
                let activeQuickReference: any = undefined;
                let activeImpairmentRating: any = undefined;
                let activeMmiStatus: any = undefined;
                let activeQmeReport: any = undefined;
                let activeMedicalLegalReport: any = undefined;
                let activePtpReport: any = undefined;
                let activeSibtfReport: any = undefined;
                let activePiReport: any = undefined;
                let activePhysicalExaminationQuestions: any = undefined;
                let activeReviewOfRecords: any = undefined;
                const currentActiveSession = store.activeSession();
                // Keep any summary/transcript body this client already fetched
                // from S3 — the session payload does not carry it back.
                const previouslyHydratedJobs =
                  currentActiveSession?.id === session.id
                    ? currentActiveSession.aiScribeJobs
                    : undefined;
                const aiScribeJobs = preserveHydratedScribeBodies(
                  previouslyHydratedJobs,
                  (sessionData.ai_scribe || [])
                    .map(mapSourceToAiScribeJob)
                    .filter(
                      (job: { status: string }) =>
                        job.status !== AiScribeStatus.DISCARDED,
                    ),
                );
                let updatedActiveAiScribeJob: any = undefined;
                if (
                  currentActiveSession?.activeAiScribeJob &&
                  currentActiveSession.id === session.id
                ) {
                  const currentId =
                    currentActiveSession.activeAiScribeJob.aiScribeId;
                  const found = aiScribeJobs.find(
                    (j: { aiScribeId: string }) => j.aiScribeId === currentId,
                  );
                  if (found) updatedActiveAiScribeJob = found;
                }

                const allOutputsToHydrate: Output[] = [];

                // Get outputs sorted by createdAt DESC (most recent first)
                normalizedSessionData.task?.forEach((t: any) => {
                  if (!t.output) return;

                  const sortedOutputs = [...t.output].sort(
                    (a: any, b: any) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime(),
                  );

                  // Use find() to get the most recent output of each type (first match after sorting)
                  if (!activeSummary)
                    activeSummary = sortedOutputs.find(
                      (o: any) => o.output_type === 'summary',
                    );
                  if (!activeQuickReference)
                    activeQuickReference = sortedOutputs.find(
                      (o: any) => o.output_type === 'quick_reference',
                    );
                  if (!activeImpairmentRating)
                    activeImpairmentRating = sortedOutputs.find(
                      (o: any) => o.output_type === 'impairment_rating',
                    );
                  // The backend only ever emits output_type 'mmi_status'; this
                  // first lookup is a defensive, currently-unreachable fallback
                  // for a value the backend has never sent.
                  if (!activeMmiStatus)
                    activeMmiStatus = sortedOutputs.find(
                      (o: any) => o.output_type === 'mmi_status_impairment',
                    );
                  if (!activeMmiStatus)
                    activeMmiStatus = sortedOutputs.find(
                      (o: any) => o.output_type === 'mmi_status',
                    );
                  if (!activeQmeReport)
                    activeQmeReport = sortedOutputs.find(
                      (o: any) => o.output_type === 'qme_report',
                    );
                  if (!activeMedicalLegalReport)
                    activeMedicalLegalReport = sortedOutputs.find(
                      (o: any) => o.output_type === 'medical_legal_report',
                    );
                  if (!activePtpReport)
                    activePtpReport = sortedOutputs.find(
                      (o: any) => o.output_type === 'ptp_report',
                    );
                  if (!activeSibtfReport)
                    activeSibtfReport = sortedOutputs.find(
                      (o: any) => o.output_type === 'sibtf_report',
                    );
                  if (!activePiReport)
                    activePiReport = sortedOutputs.find(
                      (o: any) => o.output_type === 'pi_report',
                    );
                  if (!activePhysicalExaminationQuestions)
                    activePhysicalExaminationQuestions = sortedOutputs.find(
                      (o: any) =>
                        o.output_type === 'physical_examination_questions',
                    );
                  if (!activeReviewOfRecords)
                    activeReviewOfRecords = sortedOutputs.find(
                      (o: any) => o.output_type === 'review_of_records',
                    );

                  allOutputsToHydrate.push(...sortedOutputs);
                });

                const previousSession = store.activeSession();
                const mappedSessionMembers = (
                  sessionData.SessionMember ?? []
                ).map(mapSessionMember);
                const assigneeSessionMembers = mappedSessionMembers.filter(
                  (member: { role: SessionMemberRole }) =>
                    member.role === SessionMemberRole.ASSIGNEE,
                );

                patchState(store, {
                  sessions: store.sessions().map((existingSession) =>
                    existingSession.id === session.id
                      ? {
                          ...existingSession,
                          ...normalizedSessionData,
                          claimant:
                            normalizedSessionData.claimant ??
                            existingSession.claimant,
                          sessionMembers: assigneeSessionMembers,
                        }
                      : existingSession,
                  ),
                  activeSession: {
                    ...normalizedSessionData,
                    sessionMembers: mappedSessionMembers,
                    aiScribeJobs,
                    activeAiScribeJob: updatedActiveAiScribeJob,
                    // Same-session refetch: keep the prior summary text until the
                    // S3 hydrate lands (see mergeSummaryForActivePatch).
                    summary: mergeSummaryForActivePatch(
                      activeSummary,
                      previousSession?.id === session.id
                        ? previousSession.summary
                        : undefined,
                    ),
                    quickReference: activeQuickReference,
                    impairmentRating:
                      activeImpairmentRating ??
                      previousSession?.impairmentRating,
                    mmiStatus: activeMmiStatus ?? previousSession?.mmiStatus,
                    qmeReport: activeQmeReport ?? previousSession?.qmeReport,
                    medicalLegalReport:
                      activeMedicalLegalReport ??
                      previousSession?.medicalLegalReport,
                    ptpReport: activePtpReport ?? previousSession?.ptpReport,
                    sibtfReport:
                      activeSibtfReport ?? previousSession?.sibtfReport,
                    piReport: activePiReport ?? previousSession?.piReport,
                    physicalExaminationQuestions:
                      activePhysicalExaminationQuestions,
                    // Same-session refetch: keep prior RoR row if missing; keep prior *text* if
                    // API row exists but body is empty until S3 hydrate (~sessionService.hydrateOutput).
                    reviewOfRecords: mergeReviewOfRecordsForActivePatch(
                      activeReviewOfRecords,
                      previousSession?.id === session.id
                        ? previousSession.reviewOfRecords
                        : undefined,
                    ),
                  },
                });

                const hydrateStart = performance.now();

                // Only hydrate the default-visible output (reviewOfRecords) upfront.
                // Other output types are hydrated lazily by their viewer components
                // via `hydrateOutputType()` when the user navigates to them.
                // Best-effort, reconnect/drift reconciliation: the gate-state
                // fetch is independent of the main session GET above — a
                // failure here must never break session hydration, and a
                // response that arrives after the user has switched sessions
                // must be discarded rather than applied to the wrong one.
                fetchReportFreshness(session.id);

                sessionService
                  .hydrateOutput(activeReviewOfRecords, session.id)
                  .then((reviewOfRecords) => {
                    console.log(
                      'hydrateOutputs:',
                      (performance.now() - hydrateStart).toFixed(2),
                      'ms',
                    );
                    const current = store.activeSession();
                    if (!current || current.id !== session.id) return;
                    if (!reviewOfRecords) return;
                    // Mark this output ID as already hydrated from S3 so any
                    // viewer-component effect that calls hydrateOutputType()
                    // for the same output won't refetch.
                    if (activeReviewOfRecords?.id) {
                      hydratedOutputIds.add(activeReviewOfRecords.id);
                    }
                    patchState(store, {
                      activeSession: {
                        ...current,
                        reviewOfRecords,
                      },
                    });
                    console.log(
                      'TOTAL:',
                      (performance.now() - totalStart).toFixed(2),
                      'ms',
                    );
                  })
                  .catch((err) => console.error('Hydration failed', err));
              }),
              catchError((error) => {
                console.error('Error fetching session:', error);
                if (error.status !== 401 && error.status !== 403) {
                  notification.error('Error', 'Oops! Something went wrong');
                }
                patchState(store, {
                  activeSession: null,
                });
                throw error;
              }),
            );
        },
        /**
         * Lazily hydrates a single output on the active session by fetching its
         * S3 contents. Used by viewer components (summary, qme-report, etc.) so
         * that only the output the user is actively viewing pays the
         * signed-URL + S3 fetch cost.
         */
        hydrateOutputType(
          outputKey:
            | 'summary'
            | 'quickReference'
            | 'impairmentRating'
            | 'mmiStatus'
            | 'qmeReport'
            | 'piReport'
            | 'medicalLegalReport'
            | 'ptpReport'
            | 'sibtfReport'
            | 'reviewOfRecords'
            | 'physicalExaminationQuestions',
        ): Promise<void> {
          const current = store.activeSession();
          if (!current) return Promise.resolve();

          const output = (current as any)[outputKey] as
            | Output
            | undefined
            | null;
          if (!output) return Promise.resolve();

          if (hydratedOutputIds.has(output.id)) {
            return Promise.resolve();
          }

          // Nothing to fetch from S3.
          const hasS3 = output.s3_key && output.s3_bucket && output.s3_url;
          if (!hasS3) return Promise.resolve();

          const sessionId = current.id;
          const cacheKey = `${sessionId}:${output.id}`;
          const existing = inFlightHydrations.get(cacheKey);
          if (existing) return existing;

          const promise = sessionService
            .hydrateOutput(output, sessionId)
            .then((hydrated) => {
              const latest = store.activeSession();
              if (!latest || latest.id !== sessionId) return;
              if (!hydrated) return;
              // Mark hydrated so subsequent effect runs (e.g. after another
              // patchState changes the activeSession ref) don't refetch from S3.
              hydratedOutputIds.add(output.id);
              patchState(store, {
                activeSession: {
                  ...latest,
                  [outputKey]: hydrated,
                } as Session,
              });
            })
            .catch((err) => {
              console.error(
                `[hydrateOutputType] Failed to hydrate ${outputKey}`,
                err,
              );
            })
            .finally(() => {
              inFlightHydrations.delete(cacheKey);
            });

          inFlightHydrations.set(cacheKey, promise);
          return promise;
        },
        refreshSessionMembers(sessionId: string) {
          return sessionService
            .getSessionMembers(sessionId, {
              user: { include: { qmeProfile: true } },
            })
            .pipe(
              tap((members) => {
                const mapped = members.map(mapSessionMember);
                const session = store.activeSession();
                if (session) {
                  // activeSession gets the full member list (used by member management UI)
                  // sessions[] only tracks ASSIGNEE members, matching the getSessions() fetch shape
                  // (cases table only displays the assigned QME physician)
                  const assignees = mapped.filter(
                    (m) => m.role === SessionMemberRole.ASSIGNEE,
                  );
                  patchState(store, (state) => ({
                    activeSession: { ...session, sessionMembers: mapped },
                    sessions: state.sessions.map((s) =>
                      s.id === sessionId
                        ? { ...s, sessionMembers: assignees }
                        : s,
                    ),
                  }));
                }
              }),
            );
        },
        updateSession(session: Session) {
          // store old session
          // Api call to update session
          const normalizedSession: Session = {
            ...session,
            dateOfInjury: stripTimeFromDate(
              session.dateOfInjury,
            ) as Session['dateOfInjury'],
            claimant: session.claimant
              ? {
                  ...session.claimant,
                  dateOfBirth: stripTimeFromDate(session.claimant.dateOfBirth),
                }
              : undefined,
          };
          patchState(store, (state) => ({
            sessions: state.sessions.map((s) =>
              s.id === normalizedSession.id ? normalizedSession : s,
            ),
          }));
        },
        patchSessionFields(sessionId: string, patch: Partial<Session>) {
          const beforeClaimantId = store.activeSession()?.claimant?.id ?? null;
          const hasClaimantKey = Object.hasOwn(patch, 'claimant');

          console.debug(
            '[session.store] patchSessionFields (before normalized)',
            {
              sessionId,
              patchKeys: Object.keys(patch),
              hasClaimantKey,
              beforeClaimantId,
            },
          );

          const normalizedPatch: Partial<Session> = { ...patch };

          // Normalize `dateOfInjury` only if the caller explicitly includes it.
          if (Object.hasOwn(patch, 'dateOfInjury')) {
            normalizedPatch.dateOfInjury = stripTimeFromDate(
              patch.dateOfInjury,
            ) as Session['dateOfInjury'];
          }

          // IMPORTANT claimant fix:
          // `patchSessionFields()` is used for partial updates (e.g. caseInfo).
          // If the patch does NOT include `claimant`, we must not overwrite the
          // existing `activeSession.claimant` with `undefined`, otherwise dependent
          // UI resets/clears (like the injured worker section) can occur.
          if (Object.hasOwn(patch, 'claimant')) {
            normalizedPatch.claimant = patch.claimant
              ? {
                  ...patch.claimant,
                  dateOfBirth: stripTimeFromDate(patch.claimant.dateOfBirth),
                }
              : undefined;
          }

          console.debug('[session.store] patchSessionFields (normalized)', {
            sessionId,
            normalizedClaimantId: normalizedPatch.claimant?.id ?? null,
          });
          patchState(store, (state) => ({
            sessions: state.sessions.map((s) =>
              s.id === sessionId ? { ...s, ...normalizedPatch } : s,
            ),
            activeSession:
              state.activeSession?.id === sessionId
                ? { ...state.activeSession, ...normalizedPatch }
                : state.activeSession,
          }));
        },
        getSessions(params?: {
          skip?: number;
          take?: number;
          search?: string;
          reportType?: string;
          physicianUserId?: string;
          sortBy?: 'createdAt';
          sortOrder?: 'asc' | 'desc';
        }) {
          const take = params?.take ?? 7;
          const skip = params?.skip ?? 0;
          patchState(store, {
            isLoading: true,
          });
          sessionService
            .getSessionsPage({
              skip,
              take,
              search: params?.search,
              reportType: params?.reportType,
              physicianUserId: params?.physicianUserId,
              sortBy: params?.sortBy,
              sortOrder: params?.sortOrder,
              includes: CASES_TABLE_INCLUDE,
            })
            .subscribe({
              next: (response: SessionsPageResponse) => {
                const total = response.meta.total;
                const take = response.meta.take;
                const skip = response.meta.skip;

                // If restored/requested page is past the end, refetch the last page.
                if (total > 0 && skip >= total) {
                  const lastPageIndex = Math.max(1, Math.ceil(total / take));
                  const lastSkip = (lastPageIndex - 1) * take;
                  sessionService
                    .getSessionsPage({
                      skip: lastSkip,
                      take,
                      search: params?.search,
                      reportType: params?.reportType,
                      physicianUserId: params?.physicianUserId,
                      sortBy: params?.sortBy,
                      sortOrder: params?.sortOrder,
                      includes: CASES_TABLE_INCLUDE,
                    })
                    .subscribe({
                      next: (retryResponse: SessionsPageResponse) => {
                        patchState(store, {
                          sessions: retryResponse.data.map(mapSourceSession),
                          sessionsTotal: retryResponse.meta.total,
                          physicianOptions:
                            retryResponse.physicianOptions ?? [],
                          isLoading: false,
                        });
                      },
                      error: (error) => {
                        if (error.status !== 401) {
                          notification.error(
                            'Error',
                            'Oops! Something went wrong',
                          );
                        }
                        console.error('Error fetching sessions', error);
                        patchState(store, {
                          sessions: [],
                          sessionsTotal: 0,
                          isLoading: false,
                        });
                      },
                    });
                  return;
                }

                patchState(store, {
                  sessions: response.data.map(mapSourceSession),
                  sessionsTotal: total,
                  physicianOptions: response.physicianOptions ?? [],
                  isLoading: false,
                });
              },
              error: (error) => {
                if (error.status !== 401) {
                  notification.error('Error', 'Oops! Something went wrong');
                }
                console.error('Error fetching sessions', error);
                patchState(store, {
                  sessions: [],
                  sessionsTotal: 0,
                  isLoading: false,
                });
              },
            });
        },
        getSession(sessionId: string) {
          return store.sessions().find((session) => session.id === sessionId);
        },
        updateClaimantInfo(claimant: Claimant) {
          const normalizedClaimant = {
            ...claimant,
            dateOfBirth: stripTimeFromDate(claimant.dateOfBirth),
          };
          patchState(store, (state) => ({
            sessions: state.sessions.map((s) =>
              s.claimant?.id === normalizedClaimant?.id
                ? { ...s, claimant: normalizedClaimant }
                : s,
            ),
            activeSession:
              state.activeSession?.claimant?.id === normalizedClaimant?.id
                ? { ...state.activeSession, claimant: normalizedClaimant }
                : state.activeSession,
          }));
        },
        updateActiveSessionSummary(session_id: string, newSummary: Output) {
          const activeSummarySessionId = store.activeSession()?.id;
          if (session_id === activeSummarySessionId) {
            const updatedActiveSession = store.activeSession();
            if (updatedActiveSession) {
              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  summary: newSummary,
                },
              });
            }
          }
        },
        updateActiveSessionQuickReference(
          session_id: string,
          newQuickReference: Output,
        ) {
          const activeQuickReferenceSessionId = store.activeSession()?.id;
          if (session_id === activeQuickReferenceSessionId) {
            const updatedActiveSession = store.activeSession();

            if (updatedActiveSession) {
              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  quickReference: newQuickReference,
                },
              });
            }
          }
        },
        updateActiveSessionImpairmentRating(
          session_id: string,
          newImpairmentRating: Output,
        ) {
          const activeSessionId = store.activeSession()?.id;
          if (session_id === activeSessionId) {
            const updatedActiveSession = store.activeSession();

            if (updatedActiveSession) {
              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  impairmentRating: newImpairmentRating,
                },
              });
            }
          }
        },
        /**
         * Optimistically flips the QME staleness banner/gate for the person
         * who just saved a standalone Impairment Rating or Review of Records
         * edit — the same fact the next `getMmiGateState` fetch would report
         * anyway (a manual edit always produces a new output id, so the next
         * server read is guaranteed to agree), just without waiting for a
         * page reload to find out something already known locally. Other
         * viewers of the session only see it on their own next fetch — there
         * is no push/notification mechanism, matching the staleness ticket's
         * scope.
         */
        markActiveSessionQmeInputStale(
          session_id: string,
          edited: Partial<{ impairmentEdited: boolean; rorEdited: boolean }>,
        ) {
          const current = store.activeSession();
          if (session_id !== current?.id || !current.mmiGateState) return;
          patchState(store, {
            activeSession: {
              ...current,
              mmiGateState: {
                ...current.mmiGateState,
                qmeInputStaleness: {
                  impairmentEdited:
                    current.mmiGateState.qmeInputStaleness?.impairmentEdited ??
                    false,
                  rorEdited:
                    current.mmiGateState.qmeInputStaleness?.rorEdited ?? false,
                  ...edited,
                },
              },
            },
          });
        },
        updateActiveSessionMmiStatus(session_id: string, newMmiStatus: Output) {
          const activeSessionId = store.activeSession()?.id;
          if (session_id === activeSessionId) {
            const updatedActiveSession = store.activeSession();
            if (updatedActiveSession) {
              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  mmiStatus: newMmiStatus,
                },
              });
            }
          }
        },
        updateActiveSessionQmeReport(session_id: string, newQmeReport: Output) {
          const activeQmeReportSessionId = store.activeSession()?.id;
          if (session_id === activeQmeReportSessionId) {
            const updatedActiveSession = store.activeSession();

            if (updatedActiveSession) {
              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  qmeReport: newQmeReport,
                },
              });
            }
          }
        },
        updateActiveSessionMedicalLegalReport(
          session_id: string,
          newMedicalLegalReport: Output,
        ) {
          const activeSessionId = store.activeSession()?.id;
          if (session_id === activeSessionId) {
            const updatedActiveSession = store.activeSession();
            if (updatedActiveSession) {
              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  medicalLegalReport: newMedicalLegalReport,
                },
              });
            }
          }
        },
        updateActiveSessionPtpReport(session_id: string, newPtpReport: Output) {
          const activeSessionId = store.activeSession()?.id;
          if (session_id === activeSessionId) {
            const updatedActiveSession = store.activeSession();
            if (updatedActiveSession) {
              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  ptpReport: newPtpReport,
                },
              });
            }
          }
        },
        updateActiveSessionSibtfReport(
          session_id: string,
          newSibtfReport: Output,
        ) {
          const activeSessionId = store.activeSession()?.id;
          if (session_id === activeSessionId) {
            const updatedActiveSession = store.activeSession();
            if (updatedActiveSession) {
              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  sibtfReport: newSibtfReport,
                },
              });
            }
          }
        },
        updateActiveSessionPiReport(session_id: string, newPiReport: Output) {
          const activePiReportSessionId = store.activeSession()?.id;
          if (session_id === activePiReportSessionId) {
            const updatedActiveSession = store.activeSession();

            if (updatedActiveSession) {
              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  piReport: newPiReport,
                },
              });
            }
          }
        },
        /**
         * Upsert the live report-generation status row for `outputType` on the
         * active session. The backend only refetches the session on terminal
         * states, so intermediate statuses (QUEUED / *_GENERATION_* / RETRY_*)
         * pushed over websocket would otherwise live only in transient component
         * state and be lost when a report component is destroyed (e.g. switching
         * tabs)
         */
        updateActiveSessionReportGenerationStatus(
          session_id: string,
          outputType: string,
          status: ReportGenerationStatusValue,
        ) {
          const currentActiveSession = store.activeSession();
          if (currentActiveSession?.id !== session_id) {
            return;
          }

          const target = outputType.trim().toLowerCase();
          const now = new Date();
          const existing = currentActiveSession.reportGenerationStatuses ?? [];
          const matchIndex = existing.findIndex(
            (row) => row?.outputType?.trim().toLowerCase() === target,
          );

          let nextStatuses: SessionReportGenerationStatus[];
          if (matchIndex >= 0) {
            nextStatuses = existing.map((row, idx) =>
              idx === matchIndex ? { ...row, status, updatedAt: now } : row,
            );
          } else {
            nextStatuses = [
              ...existing,
              {
                id: `local-${target}`,
                sessionId: session_id,
                outputType,
                status,
                createdAt: now,
                updatedAt: now,
              },
            ];
          }

          patchState(store, {
            activeSession: {
              ...currentActiveSession,
              reportGenerationStatuses: nextStatuses,
            },
          });
        },
        /**
         * Persist whether an MMI gate check is currently in flight, for the
         * same reason as `updateActiveSessionReportGenerationStatus` above:
         * an MMI gate check is started by a REST call and only resolves
         * later (Temporal), so if this lived only in a report component's
         * local signal it would be lost whenever that component is
         * destroyed and recreated (e.g. switching tabs and back) while the
         * check is still running. Kept independent from
         * `updateActiveSessionMmiGatePendingForceDecision` below rather than
         * one combined status — see the `MmiGateCheckState` doc comment.
         */
        updateActiveSessionMmiGateCheckInProgress(
          session_id: string,
          isChecking: boolean,
        ) {
          const currentActiveSession = store.activeSession();
          if (currentActiveSession?.id !== session_id) {
            return;
          }

          patchState(store, {
            activeSession: {
              ...currentActiveSession,
              mmiGateCheckState: {
                isChecking,
                pendingForceDecisionMmiOutputId:
                  currentActiveSession.mmiGateCheckState
                    ?.pendingForceDecisionMmiOutputId ?? null,
              },
            },
          });
        },
        /** Persist a pending "force generation anyway?" MMI decision prompt — see `updateActiveSessionMmiGateCheckInProgress` above. */
        updateActiveSessionMmiGatePendingForceDecision(
          session_id: string,
          mmiOutputId: string | null,
        ) {
          const currentActiveSession = store.activeSession();
          if (currentActiveSession?.id !== session_id) {
            return;
          }

          patchState(store, {
            activeSession: {
              ...currentActiveSession,
              mmiGateCheckState: {
                isChecking:
                  currentActiveSession.mmiGateCheckState?.isChecking ?? false,
                pendingForceDecisionMmiOutputId: mmiOutputId,
              },
            },
          });
        },
        fetchActiveSessionAiScribeJobs(session_id: string, includes?: string) {
          return aiScribeService
            .getAiScribesForSession(session_id, includes)
            .pipe(
              tap((aiScribeJobsSource: SourceAiScribeJob[]) => {
                const mappedJobs = aiScribeJobsSource
                  .map(mapSourceToAiScribeJob)
                  .filter((job) => job.status !== AiScribeStatus.DISCARDED);

                // Same reason as in the session GET: this response carries S3
                // refs but no body, so merge the hydrated one forward rather
                // than replacing it with the mapper's empty placeholder.
                const updatedSessions = store
                  .sessions()
                  .map((session) =>
                    session.id === session_id
                      ? updateSessionAiScribeJobs(
                          session,
                          preserveHydratedScribeBodies(
                            session.aiScribeJobs,
                            mappedJobs,
                          ),
                        )
                      : session,
                  );

                const currentActiveSession = store.activeSession();
                const updatedActiveSession =
                  currentActiveSession?.id === session_id
                    ? updateSessionAiScribeJobs(
                        currentActiveSession,
                        preserveHydratedScribeBodies(
                          currentActiveSession.aiScribeJobs,
                          mappedJobs,
                        ),
                      )
                    : currentActiveSession;

                patchState(store, {
                  sessions: updatedSessions,
                  activeSession: updatedActiveSession ?? null,
                });
              }),
            );
        },
        updateActiveSessionAiScribeJobs(
          session_id: string,
          newAiScribeJobs: AiScribeJob[],
        ) {
          const activeSessionId = store.activeSession()?.id;

          if (session_id === activeSessionId) {
            const currentActiveSession = store.activeSession();

            if (currentActiveSession) {
              const resolvedActiveAiScribeJob = resolveActiveAiScribeJob(
                currentActiveSession.activeAiScribeJob,
                newAiScribeJobs,
              );

              patchState(store, {
                activeSession: {
                  ...currentActiveSession,
                  aiScribeJobs: newAiScribeJobs,
                  activeAiScribeJob: resolvedActiveAiScribeJob as AiScribeJob,
                },
              });
            }
          }
        },
        /**
         * Drop a deleted AI Scribe so the sidebar and anything reading
         * activeAiScribeJob clear at once, without waiting for a refetch.
         * Also clears activeAiScribeJob if it was the one deleted.
         */
        removeAiScribeJob(sessionId: string, aiScribeId: string) {
          const removeFrom = (s: Session): Session => {
            const remainingJobs = (s.aiScribeJobs || []).filter(
              (job) => job.aiScribeId !== aiScribeId,
            );
            const activeWasDeleted =
              s.activeAiScribeJob?.aiScribeId === aiScribeId;

            return {
              ...s,
              aiScribeJobs: remainingJobs,
              activeAiScribeJob: activeWasDeleted
                ? (undefined as unknown as AiScribeJob)
                : s.activeAiScribeJob,
            };
          };

          patchState(store, (state) => ({
            sessions: state.sessions.map((s) =>
              s.id === sessionId ? removeFrom(s) : s,
            ),
            activeSession:
              state.activeSession?.id === sessionId
                ? removeFrom(state.activeSession)
                : state.activeSession,
          }));
        },
        updateJobMetadata(
          sessionId: string,
          aiScribeId: string,
          metadata: Partial<AiScribeJob>,
        ) {
          // Shallow-merge the incoming metadata, but never let it blank a body
          // this client already fetched from S3 — see
          // mergeHydratedScribeContent.
          const applyMetadata = (job: AiScribeJob): AiScribeJob => {
            const merged = { ...job, ...metadata } as AiScribeJob;
            if (metadata.AiScribeContents) {
              merged.AiScribeContents = mergeHydratedScribeContent(
                job.AiScribeContents,
                metadata.AiScribeContents,
              ) as AiScribeContents;
            }
            return merged;
          };

          const updateSession = (s: Session) => {
            const updatedJobs = (s.aiScribeJobs || []).map((j) =>
              j.aiScribeId === aiScribeId ? applyMetadata(j) : j,
            );
            // Keep the currently-viewed job in sync too, otherwise the open tab
            // reads a stale activeAiScribeJob and the newly generated summary /
            // status only appears after a manual page refresh.
            const updatedActiveAiScribeJob =
              s.activeAiScribeJob?.aiScribeId === aiScribeId
                ? applyMetadata(s.activeAiScribeJob)
                : s.activeAiScribeJob;
            return {
              ...s,
              aiScribeJobs: updatedJobs,
              activeAiScribeJob: updatedActiveAiScribeJob,
            };
          };

          patchState(store, (state) => ({
            sessions: state.sessions.map((s) =>
              s.id === sessionId ? updateSession(s) : s,
            ),
            activeSession:
              state.activeSession?.id === sessionId
                ? updateSession(state.activeSession)
                : state.activeSession,
          }));
        },
        setActiveSessionActiveAiScribe(
          session_id: string,
          aiScribeJob: AiScribeJob,
        ) {
          const currentSessions = store.sessions();
          const currentActiveSession = store.activeSession();

          const updatedSessions = currentSessions.map((s) => {
            if (s.id === session_id) {
              // Logic to upsert the job into this session's list
              const jobs = s.aiScribeJobs || [];
              const exists = jobs.find(
                (j) => j.aiScribeId === aiScribeJob.aiScribeId,
              );
              const updatedJobs = exists
                ? jobs.map((j) =>
                    j.aiScribeId === aiScribeJob.aiScribeId ? aiScribeJob : j,
                  )
                : [aiScribeJob, ...jobs];

              return { ...s, aiScribeJobs: updatedJobs };
            }
            return s;
          });

          if (currentActiveSession?.id === session_id) {
            const jobs = currentActiveSession.aiScribeJobs || [];
            const exists = jobs.find(
              (j) => j.aiScribeId === aiScribeJob.aiScribeId,
            );
            const updatedJobs = exists
              ? jobs.map((j) =>
                  j.aiScribeId === aiScribeJob.aiScribeId ? aiScribeJob : j,
                )
              : [aiScribeJob, ...jobs];

            patchState(store, {
              sessions: updatedSessions,
              activeSession: {
                ...currentActiveSession,
                aiScribeJobs: updatedJobs,
                activeAiScribeJob: aiScribeJob,
              },
            });
          } else {
            patchState(store, { sessions: updatedSessions });
          }
        },
        updateActiveSessionPhysicalExaminationQuestions(
          session_id: string,
          newPhysicalExaminationQuestions: Output,
        ) {
          const activePhysicalExamSessionId = store.activeSession()?.id;
          if (session_id === activePhysicalExamSessionId) {
            const updatedActiveSession = store.activeSession();

            if (updatedActiveSession) {
              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  physicalExaminationQuestions: newPhysicalExaminationQuestions,
                },
              });
            }
          }
        },
        updateActiveSessionReviewOfRecords(
          session_id: string,
          newReviewOfRecords: Output,
        ) {
          const activeReviewOfRecordsSessionId = store.activeSession()?.id;
          if (session_id === activeReviewOfRecordsSessionId) {
            const updatedActiveSession = store.activeSession();

            if (updatedActiveSession) {
              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  reviewOfRecords: newReviewOfRecords,
                },
              });
            }
          }
        },
        updateDocumentOcrOutput(
          session_id: string,
          document_id: string,
          ocr_output: any,
        ) {
          const activeSessionId = store.activeSession()?.id;
          if (session_id === activeSessionId) {
            const updatedActiveSession = store.activeSession();

            if (updatedActiveSession?.documents) {
              const updatedDocuments = updatedActiveSession.documents.map(
                (doc) =>
                  doc.id === document_id
                    ? { ...doc, ocrOutput: ocr_output }
                    : doc,
              );

              patchState(store, {
                activeSession: {
                  ...updatedActiveSession,
                  documents: updatedDocuments,
                },
              });
            }
          }
        },

        /**
         * Reread the per-report freshness for sessionId after there is change to
         * input set those reports are compared against
         *
         * Not needed on report generation since that will fetch the session again
         */
        refreshReportFreshness(sessionId: string) {
          // Guarded on both ends: here, so an event for a non-active session
          // cant cancel a pending refresh for the active one and again when
          // the timer fires, in case the user switched sessions mid-window.
          if (store.activeSession()?.id !== sessionId) return;
          if (reportFreshnessTimer) {
            clearTimeout(reportFreshnessTimer);
          }
          reportFreshnessTimer = setTimeout(() => {
            reportFreshnessTimer = null;
            if (store.activeSession()?.id !== sessionId) return;
            fetchReportFreshness(sessionId);
          }, REPORT_FRESHNESS_DEBOUNCE_MS);
        },

        clearActiveSession() {
          patchState(store, {
            activeSession: null,
          });
        },
        reset() {
          patchState(store, initialState);
        },
        setAiScribeInputTextById(aiScribeId: string, inputText: string): void {
          const updateSession = (session: Session): Session => {
            const aiScribeJobs = session.aiScribeJobs ?? [];
            let didUpdate = false;

            const updatedJobs = aiScribeJobs.map((job) => {
              if (job.aiScribeId !== aiScribeId) {
                return job;
              }

              didUpdate = true;
              return {
                ...job,
                AiScribeContents: {
                  ...job.AiScribeContents,
                  inputText,
                },
              };
            });

            if (!didUpdate) {
              return session;
            }

            const updatedActiveJob =
              session.activeAiScribeJob?.aiScribeId === aiScribeId
                ? (updatedJobs.find((job) => job.aiScribeId === aiScribeId) ??
                  session.activeAiScribeJob)
                : session.activeAiScribeJob;

            return {
              ...session,
              aiScribeJobs: updatedJobs,
              activeAiScribeJob: updatedActiveJob,
            };
          };

          patchState(store, (state) => ({
            sessions: state.sessions.map(updateSession),
            activeSession: state.activeSession?.aiScribeJobs?.some(
              (job) => job.aiScribeId === aiScribeId,
            )
              ? updateSession(state.activeSession)
              : state.activeSession,
          }));
        },
        /**
         * Writes ONLY the summary body onto a scribe's collapsed content.
         *
         * Distinct from setAiScribeOutputById, which also overwrites
         * inputText: this is the landing site for the lazy S3 hydration that
         * replaced generatedOutput in the session payload, and the transcript
         * is fetched on its own schedule. Clobbering inputText here would
         * blank a transcript that had already loaded.
         */
        setAiScribeGeneratedOutputById(
          aiScribeId: string,
          output: string | null,
        ): void {
          const updateSession = (session: Session): Session => {
            const aiScribeJobs = session.aiScribeJobs ?? [];
            let didUpdate = false;

            const updatedJobs = aiScribeJobs.map((job) => {
              if (job.aiScribeId !== aiScribeId) {
                return job;
              }

              didUpdate = true;
              return {
                ...job,
                AiScribeContents: {
                  ...job.AiScribeContents,
                  generatedOutput: {
                    ...job.AiScribeContents?.generatedOutput,
                    output,
                  },
                },
              };
            });

            if (!didUpdate) {
              return session;
            }

            const updatedActiveJob =
              session.activeAiScribeJob?.aiScribeId === aiScribeId
                ? (updatedJobs.find((job) => job.aiScribeId === aiScribeId) ??
                  session.activeAiScribeJob)
                : session.activeAiScribeJob;

            return {
              ...session,
              aiScribeJobs: updatedJobs,
              activeAiScribeJob: updatedActiveJob,
            };
          };

          patchState(store, (state) => ({
            sessions: state.sessions.map(updateSession),
            activeSession: state.activeSession?.aiScribeJobs?.some(
              (job) => job.aiScribeId === aiScribeId,
            )
              ? updateSession(state.activeSession)
              : state.activeSession,
          }));
        },
        setAiScribeOutputById(
          aiScribeId: string,
          input_text: string,
          output: string | null,
        ): void {
          const session = store.activeSession();
          if (!session) return;

          const aiScribeJobs = session.aiScribeJobs ?? [];

          const updatedJobs = aiScribeJobs.map((job) => {
            if (job.aiScribeId !== aiScribeId) return job;

            return {
              ...job,
              AiScribeContents: {
                ...job.AiScribeContents,
                inputText: input_text,
                generatedOutput: {
                  ...job.AiScribeContents?.generatedOutput,
                  output,
                },
              },
            };
          });

          const updatedActiveJob =
            session.activeAiScribeJob?.aiScribeId === aiScribeId
              ? (updatedJobs.find((j) => j.aiScribeId === aiScribeId) ??
                session.activeAiScribeJob)
              : session.activeAiScribeJob;

          patchState(store, {
            activeSession: {
              ...session,
              aiScribeJobs: updatedJobs,
              activeAiScribeJob: updatedActiveJob,
            },
          });
        },

        // ── Pending docx template selection ───────────────────────────────────
        setPendingDocxTemplateSelection(
          sessionId: string,
          targetId: string,
        ): void {
          pendingDocxTemplateSelections.set(sessionId, targetId);
        },

        getPendingDocxTemplateSelection(sessionId: string): string | undefined {
          return pendingDocxTemplateSelections.get(sessionId);
        },

        clearPendingDocxTemplateSelection(sessionId: string): void {
          pendingDocxTemplateSelections.delete(sessionId);
        },
        // ─────────────────────────────────────────────────────────────────────
      };
    },
  ),
);
